"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer({
  isMobile = false,
  onItemClick
}: {
  isMobile?: boolean;
  onItemClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className={`flex ${isMobile ? "flex-col" : "items-center"} gap-4`}>
        <div className="h-8 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-8 w-24 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  if (isMobile) {
    return <div className="flex flex-col gap-2 p-4">
        {menuItems.map((item, i) => <div key={item.id} data-index={i}>
            <Link to={item.href || "#"} className="block py-3 text-lg font-default font-medium text-foreground/80 hover:text-primary transition-colors border-b border-border/50" onClick={onItemClick}>
              {item.label}
            </Link>
          </div>)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-sm font-default font-medium text-foreground/80 hover:text-primary transition-colors bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="264872" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/40" : "border-b border-transparent"}`}>
      <header className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 flex items-center">
          <Link to="/" className="flex items-center gap-2 group">
            <WvcLogo className="h-7 w-auto transition-transform duration-300 group-hover:scale-105 text-primary" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="43">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          <Button className="hidden md:inline-flex bg-primary text-primary-foreground font-default font-semibold rounded-md hover:opacity-90 transition-all shadow-md shadow-primary/10">
            Book Appointment
          </Button>
          
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent/50 rounded-md" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </header>

      {}
      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-md border-b border-border/40 md:hidden shadow-xl">
          <MenuProvider menu_id="43">
            <HeaderMenuConsumer isMobile onItemClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="p-4 pt-2 pb-6">
            <Button className="w-full bg-primary text-primary-foreground font-default font-semibold rounded-md shadow-md shadow-primary/10">
              Book Appointment
            </Button>
          </div>
        </div>}
    </section>;
}